import { QueryResult } from '@apollo/client';
import { Dispatch, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type GraphqlPagination<D> = {
  data: D[];
  hasMore: boolean;
  loading: boolean;
  fetchMore: () => Promise<void>;
  refetch: VoidFunction;
  setPollInterval: Dispatch<number>;
  startPolling: (interval: number) => void;
  stopPolling: () => void;
};

export const useGraphqlPagination = <T, D>(
  query: QueryResult<T>,
  dataKey: keyof QueryResult<T>['data'],
  idKey: keyof D = null,
  options: { pollInterval: number } = { pollInterval: null },
): GraphqlPagination<D> => {
  const [hasMore, setHasMore] = useState(true);
  const [pollInterval, setPollInterval] = useState(options.pollInterval);
  const [initialLimit] = useState((query.variables.limit as number) || 30);
  const intervalId = useRef<ReturnType<typeof setInterval> | undefined>();

  // Stable identity for the empty-array fallback so consumers can put `data` in dep arrays.
  const dataArray = useMemo(() => (query.data?.[dataKey] as D[]) || [], [query.data, dataKey]);

  // Refs so callbacks below can be useCallback with primitive-only deps and stay stable across
  // renders. Without this, every render produces fresh fetchMore/refetch closures and consumers
  // can't safely depend on them.
  const queryRef = useRef(query);
  queryRef.current = query;
  const dataArrayRef = useRef(dataArray);
  dataArrayRef.current = dataArray;
  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;
  const pollIntervalRef = useRef(pollInterval);
  pollIntervalRef.current = pollInterval;

  useEffect(() => {
    setHasMore(true);
  }, [query]);

  const clearPollingInterval = useCallback(() => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
      intervalId.current = undefined;
    }
  }, []);

  useEffect(() => {
    clearPollingInterval();

    if (!pollInterval) {
      return clearPollingInterval;
    }

    intervalId.current = setInterval(() => {
      const count = Math.max(dataArrayRef.current.length, initialLimit);
      void queryRef.current.refetch({
        ...queryRef.current.variables,
        limit: count,
      });
    }, pollInterval);

    return clearPollingInterval;
  }, [pollInterval, initialLimit, clearPollingInterval]);

  const stopPolling = useCallback(() => setPollInterval(null), []);
  const startPolling = useCallback((interval: number) => setPollInterval(interval), []);

  const onFetchMore = useCallback(async () => {
    const q = queryRef.current;
    const data = dataArrayRef.current;
    // Capture the current polling interval before stopping, so we can restore it after.
    const intervalToRestore = pollIntervalRef.current;
    if (data.length < initialLimit || !hasMoreRef.current) {
      setHasMore(false);
      return;
    }
    stopPolling();
    const { data: fetchMoreData } = await q.fetchMore({
      variables: {
        cursor: idKey ? data[data.length - 1][idKey] : data[data.length - 1],
        limit: initialLimit,
      },
    });
    if ((fetchMoreData?.[dataKey] as []).length < q.variables.limit) {
      setHasMore(false);
    }
    if (intervalToRestore) startPolling(intervalToRestore);
  }, [dataKey, idKey, initialLimit, startPolling, stopPolling]);

  const onRefetch = useCallback(
    async (additional = 0) => {
      const q = queryRef.current;
      const intervalToRestore = pollIntervalRef.current;
      stopPolling();
      await q.refetch({
        ...q.variables,
        limit: Math.max(initialLimit, dataArrayRef.current.length + additional),
      });
      if (intervalToRestore) startPolling(intervalToRestore);
    },
    [initialLimit, startPolling, stopPolling],
  );

  return useMemo(
    () => ({
      data: dataArray,
      loading: query.loading,
      fetchMore: onFetchMore,
      hasMore,
      refetch: onRefetch,
      setPollInterval,
      startPolling,
      stopPolling,
    }),
    [dataArray, query.loading, onFetchMore, hasMore, onRefetch, startPolling, stopPolling],
  );
};
